import React from 'react';
import {
  BarsOutlined,
  CalendarOutlined,
  ProjectOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { ROUTE_PATHS } from '../../config/RoutePaths';

/**
 * A view area is one menu entry with several layouts of the same data, each on
 * its own route: the menu shows the area once, the page header switches between
 * the layouts, and the entry opens whichever layout the user had open last.
 * The first view is the fallback, so it should be the one most people want.
 */
export const VIEW_AREAS = {
  PROJECTS: 'projects',
  CALENDAR: 'calendar',
} as const;

export type ViewArea = (typeof VIEW_AREAS)[keyof typeof VIEW_AREAS];

export type AreaView = {
  path: string;
  icon: React.ReactNode;
  labelId: string;
  defaultLabel: string;
};

export const AREA_VIEWS: Record<ViewArea, AreaView[]> = {
  [VIEW_AREAS.PROJECTS]: [
    {
      path: ROUTE_PATHS.LEADS,
      icon: <BarsOutlined />,
      labelId: 'menu.Customers.Leads',
      defaultLabel: 'Table',
    },
    {
      path: ROUTE_PATHS.LEADS_BOARD,
      icon: <ProjectOutlined />,
      labelId: 'menu.Customers.Board',
      defaultLabel: 'Board',
    },
  ],
  [VIEW_AREAS.CALENDAR]: [
    {
      path: ROUTE_PATHS.CALENDAR,
      icon: <CalendarOutlined />,
      labelId: 'menu.Calendar',
      defaultLabel: 'Calendar',
    },
    {
      path: ROUTE_PATHS.APPOINTMENTS,
      icon: <UnorderedListOutlined />,
      labelId: 'common.list',
      defaultLabel: 'List',
    },
  ],
};

export const isAreaViewPath = (area: ViewArea, path: string): boolean =>
  AREA_VIEWS[area].some((view) => view.path === path);

/** The route an area's entry opens: the remembered view if it is still one of the area's, else the first. */
export function getAreaEntryPath(area: ViewArea, lastViews?: Partial<Record<ViewArea, string>> | null): string {
  const remembered = lastViews?.[area];
  return remembered && isAreaViewPath(area, remembered) ? remembered : AREA_VIEWS[area][0].path;
}
